import { HashSet } from "../Utils/HashSet";
import { IpSubnet } from "./IpSubnet";
import { IpAddress, IpVersion } from "./IpAddress";
import { Device } from "./Device";
import { DeviceSet } from "./DeviceSet";
import { ObjectSerializer } from "../Serialization/ObjectSerializer";
import { ObjSerUtils } from "../Serialization/ObjSerUtils";
import { ValueEncoder } from "../Utils/ValueEncoder";

//export interface IpSubnetSetEntryJson {
//    ip_subnet: string;
//}

//export interface IpSubnetSetJson {
//    entries: IpSubnetSetEntryJson[];
//}

export class IpSubnetSet implements ObjectSerializer {
	// SET of SUBNETS (sorted)
	private _subnetSet: HashSet<IpSubnet>;

	// INDEXES of NETWORK/BROADCAST addresses
	private _networkAddresses: HashSet<IpAddress>;
	private _broadcastAddresses: HashSet<IpAddress>;

	constructor() {
		this._subnetSet = new HashSet<IpSubnet>();

		// INDEXES
		this._networkAddresses = new HashSet<IpAddress>();
		this._broadcastAddresses = new HashSet<IpAddress>();
	}

	public Add(ipSubnet: IpSubnet): void {
		if (!this._subnetSet.Contains(ipSubnet)) {
			// UPDATE the INDEXES
			var ipKey: IpAddress = ipSubnet.NetAddress;
			if (ipKey != null)
				this._networkAddresses.Add(ipKey);

			ipKey = ipSubnet.MaxAddress;
			if (ipKey != null)
				this._broadcastAddresses.Add(ipKey);

			// ADD the IpSubnet
			this._subnetSet.Add(ipSubnet);
		}
	}

	private Add_IpAddressTable(device: Device): void {
		if (device == null)
			return;
		if (!device.HasIpAddressTable)
			return;

		var ipSubnet: IpSubnet = null;

		device.IpAddressTable.ForEach((ipEntry) => {
			if (ipEntry.IpAddress.IpVersion == IpVersion.IPv6)
				return;

			if (!ipEntry.IpAddress.IsValidDiscoveryAddress)
				return;

			if (ipEntry.HasNetMask && ipEntry.NetMask.IsNull)
				return;

			ipSubnet = ipEntry.IpSubnet;
			if (ipSubnet == null)
				return;

			this.Add(ipSubnet);
		});
	}

	private Add_VirtualHost_Nics(device: Device): void {
		if (device == null)
			return;
		if (!device.HasVirtualHostInfo)
			return;

		var ipSubnet: IpSubnet = null;

		if (device.VirtualHostInfo.PhysicalNics != null) {
			device.VirtualHostInfo.PhysicalNics.ForEach((pnicEntry) => {
				if (!pnicEntry.HasIpAddress)
					return;
				if (pnicEntry.IpAddress.IpVersion == IpVersion.IPv6)
					return;

				if (!pnicEntry.IpAddress.IsValidDiscoveryAddress)
					return;

				if (pnicEntry.HasSubnetMask && pnicEntry.SubnetMask.IsNull)
					return;

				ipSubnet = pnicEntry.IpSubnet;
				if (ipSubnet == null)
					return;

				this.Add(ipSubnet);
			});
		}

		if (device.VirtualHostInfo.VirtualNics != null) {
			device.VirtualHostInfo.VirtualNics.ForEach((vnicEntry) => {
				if (!vnicEntry.HasIpAddress)
					return;
				if (vnicEntry.IpAddress.IpVersion == IpVersion.IPv6)
					return;

				if (!vnicEntry.IpAddress.IsValidDiscoveryAddress)
					return;

				if (vnicEntry.HasSubnetMask && vnicEntry.SubnetMask.IsNull)
					return;

				ipSubnet = vnicEntry.IpSubnet;
				if (ipSubnet == null)
					return;

				this.Add(ipSubnet);
			});
		}
	}

	public AddDevice(device: Device): void {
		if (device == null)
			return;

		this.Add_IpAddressTable(device);
		this.Add_VirtualHost_Nics(device);
	}

	public Clear(): void {
		// INDEXES
		this._networkAddresses.Clear();
		this._broadcastAddresses.Clear();

		// SET
		this._subnetSet.Clear();
	}

	public get Count(): number {
		return this._subnetSet.Count;
	}

	public IsBroadcastAddress(ipAddress: IpAddress): boolean {
		if (ipAddress == null)
			return false;

		if (this._broadcastAddresses.Count > 0) {
			if (this._broadcastAddresses.Contains(ipAddress))
				return true;
		}

		return false;
	}

	public Remove(ipSubnet: IpSubnet): void {
		if (this._subnetSet.Contains(ipSubnet)) {
			// CLEAN-UP indexes
			var ipAddressKey: IpAddress = ipSubnet.MinAddress;
			if ((ipAddressKey != null) && this._networkAddresses.Contains(ipAddressKey))
				this._networkAddresses.Remove(ipAddressKey);

			ipAddressKey = ipSubnet.MaxAddress;
			if ((ipAddressKey != null) && this._broadcastAddresses.Contains(ipAddressKey))
				this._broadcastAddresses.Remove(ipAddressKey);

			// CLEAN-UP object
			this._subnetSet.Remove(ipSubnet);
		}
	}

	public Rebuild(deviceSet: DeviceSet): void {
		this.Clear();

		deviceSet.ForEach((device) => {
			this.AddDevice(device);
		});
	}

	public ForEach(callbackfn: (value: IpSubnet, index: number, array: IpSubnet[]) => void, thisArg?: any): void {
		this._subnetSet.ForEach(callbackfn, thisArg);
	}

	public Some(callbackfn: (value: IpSubnet, index: number, array: IpSubnet[]) => boolean, thisArg?: any): boolean {
		return this._subnetSet.Some(callbackfn, thisArg);
	}

	public Every(callbackfn: (value: IpSubnet, index: number, array: IpSubnet[]) => boolean, thisArg?: any): boolean {
		return this._subnetSet.Every(callbackfn, thisArg);
    }

    //public static FromJson(json: IpSubnetSetJson): IpSubnetSet {
    //    var result = new IpSubnetSet();

    //    if (json.entries !== undefined) {
    //        json.entries.forEach((entry) => {
    //            result.Add(IpSubnet.CreateFromString(entry.ip_subnet));
    //        }, this);
    //    }

    //    return result;
    //}

	public static readonly ObjSer_ObjectName = "IpSubnetSet_ObjectName";

	public Write(obj: any, context: any): void {
		let objName = "ip_subnets";
		if (context[IpSubnetSet.ObjSer_ObjectName] !== undefined) {
			objName = context[IpSubnetSet.ObjSer_ObjectName];
		}

		obj[objName] = {};
		obj = obj[objName];

		obj.entries = [];
		ObjSerUtils.WriteCollection(obj.entries, this._subnetSet, (value: IpSubnet) => {
			return { ip_subnet: ValueEncoder.EncodeIpSubnet(value) };
		}, context);
	}

	public Read(obj: any, context: any): void {
		let objName = "ip_subnets";
		if (context[IpSubnetSet.ObjSer_ObjectName] !== undefined) {
			objName = context[IpSubnetSet.ObjSer_ObjectName];
		}

		if (!obj[objName])
			throw new Error();
		obj = obj[objName];

		this.Clear();

		if (obj.entries !== undefined) {
			obj.entries.forEach((value: any) => {
				let subnet = ValueEncoder.DecodeIpSubnet(value.ip_subnet);
				this.Add(subnet);
			}, this);
		}
	}
}
